// RBAC Type Definitions

export enum UserRole {
  ROOT = 'ROOT',
  ADMIN = 'ADMIN',
  SUPERVISOR = 'SUPERVISOR',
  MARKETING_SUPPORT = 'MARKETING_SUPPORT',
  SHOP_SUPPORT = 'SHOP_SUPPORT',
  ORDER_SUPPORT = 'ORDER_SUPPORT',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT'
}

export type RoleType = keyof typeof UserRole

// Module access permissions
export enum Module {
  DASHBOARD = 'DASHBOARD',
  MARKETING = 'MARKETING',
  CATALOG = 'CATALOG',
  ORDERS = 'ORDERS',
  SHIPPING = 'SHIPPING',
  PAYMENTS = 'PAYMENTS',
  TICKETS = 'TICKETS',
  CUSTOMERS = 'CUSTOMERS',
  USERS = 'USERS',
  SYSTEM_ALERTS = 'SYSTEM_ALERTS',
  CMS = 'CMS',
  REPORTS = 'REPORTS',
  SETTINGS = 'SETTINGS',
  OTP = 'OTP'
}

// Action permissions
export enum Permission {
  VIEW = 'VIEW',
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  ARCHIVE = 'ARCHIVE',
  EXPORT = 'EXPORT',
  MANAGE_USERS = 'MANAGE_USERS',
  BLOCK_USERS = 'BLOCK_USERS',
  VIEW_LOGS = 'VIEW_LOGS'
}

// Role-based module access mapping
export const ROLE_MODULE_ACCESS: Record<UserRole, Module[]> = {
  [UserRole.ROOT]: [
    Module.DASHBOARD,
    Module.MARKETING,
    Module.CATALOG,
    Module.ORDERS,
    Module.SHIPPING,
    Module.PAYMENTS,
    Module.TICKETS,
    Module.CUSTOMERS,
    Module.USERS,
    Module.SYSTEM_ALERTS,
    Module.CMS,
    Module.REPORTS,
    Module.SETTINGS,
    Module.OTP
  ],

  [UserRole.ADMIN]: [
    Module.DASHBOARD,
    Module.MARKETING,
    Module.CATALOG,
    Module.ORDERS,
    Module.SHIPPING,
    Module.PAYMENTS,
    Module.TICKETS,
    Module.CUSTOMERS,
    Module.USERS,
    Module.SYSTEM_ALERTS,
    Module.CMS,
    Module.REPORTS,
    Module.SETTINGS,
    Module.OTP
  ],
  [UserRole.SUPERVISOR]: [
    Module.DASHBOARD,
    Module.MARKETING,
    Module.CATALOG,
    Module.ORDERS,
    Module.SHIPPING,
    Module.PAYMENTS,
    Module.TICKETS,
    Module.CUSTOMERS,
    Module.USERS, // Limited to Support staff only
    Module.SYSTEM_ALERTS,
    Module.REPORTS,
    Module.SETTINGS,
    Module.OTP
  ],
  [UserRole.MARKETING_SUPPORT]: [Module.MARKETING],
  [UserRole.SHOP_SUPPORT]: [Module.CATALOG],
  [UserRole.ORDER_SUPPORT]: [Module.ORDERS],
  [UserRole.CUSTOMER_SUPPORT]: [Module.TICKETS, Module.CUSTOMERS]
}

// Role-based permissions for actions
export const ROLE_PERMISSIONS: Record<UserRole, Record<Module, Permission[]>> = {
  [UserRole.ROOT]: {
    [Module.DASHBOARD]: [Permission.VIEW],
    [Module.MARKETING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CATALOG]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE, Permission.ARCHIVE],
    [Module.ORDERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.EXPORT],
    [Module.SHIPPING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.PAYMENTS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.TICKETS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CUSTOMERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.BLOCK_USERS],
    [Module.USERS]: [
      Permission.VIEW,
      Permission.CREATE,
      Permission.EDIT,
      Permission.DELETE,
      Permission.MANAGE_USERS,
      Permission.VIEW_LOGS
    ],
    [Module.SYSTEM_ALERTS]: [Permission.VIEW],
    [Module.CMS]: [Permission.VIEW, Permission.EDIT],
    [Module.REPORTS]: [Permission.VIEW, Permission.EXPORT],
    [Module.SETTINGS]: [Permission.VIEW, Permission.EDIT],
    [Module.OTP]: [Permission.VIEW, Permission.EDIT]
  },
  [UserRole.ADMIN]: {
    [Module.DASHBOARD]: [Permission.VIEW],
    [Module.MARKETING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CATALOG]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE, Permission.ARCHIVE],
    [Module.ORDERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.EXPORT],
    [Module.SHIPPING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.PAYMENTS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.TICKETS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CUSTOMERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.BLOCK_USERS],
    [Module.USERS]: [
      Permission.VIEW,
      Permission.CREATE,
      Permission.EDIT,
      Permission.DELETE,
      Permission.MANAGE_USERS,
      Permission.VIEW_LOGS
    ],
    [Module.SYSTEM_ALERTS]: [Permission.VIEW],
    [Module.CMS]: [Permission.VIEW, Permission.EDIT],
    [Module.REPORTS]: [Permission.VIEW, Permission.EXPORT],
    [Module.SETTINGS]: [Permission.VIEW, Permission.EDIT],
    [Module.OTP]: [Permission.VIEW, Permission.EDIT]
  },
  [UserRole.SUPERVISOR]: {
    [Module.DASHBOARD]: [Permission.VIEW],
    [Module.MARKETING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CATALOG]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE, Permission.ARCHIVE],
    [Module.ORDERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.EXPORT],
    [Module.SHIPPING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.PAYMENTS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.TICKETS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CUSTOMERS]: [Permission.VIEW, Permission.EDIT, Permission.DELETE, Permission.BLOCK_USERS],
    [Module.USERS]: [
      Permission.VIEW,
      Permission.CREATE,
      Permission.EDIT,
      Permission.MANAGE_USERS,
      Permission.VIEW_LOGS
    ], // Limited to Support only
    [Module.SYSTEM_ALERTS]: [Permission.VIEW],
    [Module.CMS]: [], // No access
    [Module.REPORTS]: [Permission.VIEW, Permission.EXPORT],
    [Module.SETTINGS]: [Permission.VIEW, Permission.EDIT],
    [Module.OTP]: [Permission.VIEW, Permission.EDIT]
  },
  [UserRole.MARKETING_SUPPORT]: {
    [Module.DASHBOARD]: [],
    [Module.MARKETING]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.DELETE],
    [Module.CATALOG]: [],
    [Module.ORDERS]: [],
    [Module.SHIPPING]: [],
    [Module.PAYMENTS]: [],
    [Module.TICKETS]: [],
    [Module.CUSTOMERS]: [],
    [Module.USERS]: [],
    [Module.SYSTEM_ALERTS]: [],
    [Module.CMS]: [],
    [Module.REPORTS]: [],
    [Module.SETTINGS]: [],
    [Module.OTP]: []
  },
  [UserRole.SHOP_SUPPORT]: {
    [Module.DASHBOARD]: [],
    [Module.MARKETING]: [],
    [Module.CATALOG]: [Permission.VIEW, Permission.CREATE, Permission.EDIT, Permission.ARCHIVE], // No DELETE
    [Module.ORDERS]: [],
    [Module.SHIPPING]: [],
    [Module.PAYMENTS]: [],
    [Module.TICKETS]: [],
    [Module.CUSTOMERS]: [],
    [Module.USERS]: [],
    [Module.SYSTEM_ALERTS]: [],
    [Module.CMS]: [],
    [Module.REPORTS]: [],
    [Module.SETTINGS]: [],
    [Module.OTP]: []
  },
  [UserRole.ORDER_SUPPORT]: {
    [Module.DASHBOARD]: [],
    [Module.MARKETING]: [],
    [Module.CATALOG]: [],
    [Module.ORDERS]: [Permission.VIEW, Permission.EDIT, Permission.EXPORT], // Cannot see sales summaries
    [Module.SHIPPING]: [],
    [Module.PAYMENTS]: [],
    [Module.TICKETS]: [],
    [Module.CUSTOMERS]: [],
    [Module.USERS]: [],
    [Module.SYSTEM_ALERTS]: [],
    [Module.CMS]: [],
    [Module.REPORTS]: [],
    [Module.SETTINGS]: [],
    [Module.OTP]: []
  },
  [UserRole.CUSTOMER_SUPPORT]: {
    [Module.DASHBOARD]: [],
    [Module.MARKETING]: [],
    [Module.CATALOG]: [],
    [Module.ORDERS]: [],
    [Module.SHIPPING]: [],
    [Module.PAYMENTS]: [],
    [Module.TICKETS]: [Permission.VIEW, Permission.CREATE, Permission.EDIT],
    [Module.CUSTOMERS]: [Permission.VIEW, Permission.EDIT],
    [Module.USERS]: [],
    [Module.SYSTEM_ALERTS]: [],
    [Module.CMS]: [],
    [Module.REPORTS]: [],
    [Module.SETTINGS]: [],
    [Module.OTP]: []
  }
}

// Route access mapping
export const MODULE_ROUTES: Record<Module, string[]> = {
  [Module.DASHBOARD]: ['/dashboard'],
  [Module.MARKETING]: ['/marketing/intro-ad', '/marketing/banners', '/marketing/push-campaigns', '/marketing/reels'],
  [Module.CATALOG]: [
    '/catalog/products',
    '/catalog/categories',
    '/catalog/brands',
    '/catalog/attributes',
    '/catalog/reviews'
  ],
  [Module.ORDERS]: ['/orders'],
  [Module.SHIPPING]: ['/shipping'],
  [Module.PAYMENTS]: ['/payments/methods'],
  [Module.TICKETS]: ['/tickets'],
  [Module.CUSTOMERS]: ['/customers'],
  [Module.USERS]: ['/users'],
  [Module.SYSTEM_ALERTS]: ['/system-alerts'],
  [Module.CMS]: ['/cms/help'],
  [Module.REPORTS]: ['/reports'],
  [Module.SETTINGS]: ['/settings'],
  [Module.OTP]: ['/otp/sms', '/otp/email']
}

// User type with role
export interface RBACUser {
  id: string
  name: string | null
  email: string | null
  role: UserRole
  image: string | null
  isActive: boolean
  isBlocked: boolean
  lastActive: Date | null
}

// Helper type for permission checking
export type PermissionCheck = {
  module: Module
  permission: Permission
}
